import { PagedResponseDto } from "../../../../shared/dtos/paged-response.dto";
import { IFilter } from "../../../../shared/interface";
import { BaseService } from "../../../../shared/services";
import { buildFilterCondition } from "../../../../shared/utils";
import { fetchAllPages } from "../../../../shared/utils/fetch-all-pages";
import { ICropRotationDto } from "../dtos/crop-rotation.dto";
import { IFarmLandDto } from "../dtos/farm-land.dto";

const CROP_ROTATIONS_FETCH_ALL_PAGE_SIZE = 1000;
const DECIMAL_INTEGER_PATTERN = /^-?\d+$/;

class FarmLandsService extends BaseService<IFarmLandDto, never> {
  private path = "FarmLandsV2";

  async get(id: string): Promise<IFarmLandDto> {
    return this.request([this.path, id].join("/"), { method: "GET" });
  }

  async getByArcGisId(id: string): Promise<IFarmLandDto | undefined> {
    if (!DECIMAL_INTEGER_PATTERN.test(id)) {
      return undefined;
    }
    const arcgisObjectId = Number(id);
    if (!Number.isSafeInteger(arcgisObjectId)) {
      return undefined;
    }

    const response = await this.request<PagedResponseDto<IFarmLandDto>, string>(
      this.path,
      {
        method: "GET",
        searchParams: {
          pageNumber: 1,
          pageSize: 1,
          filter: buildFilterCondition("arcgisObjectId", "==", arcgisObjectId),
        },
      }
    );
    return response.data[0];
  }

  async list(_filter: never, _include?: unknown): Promise<IFarmLandDto[]> {
    throw new Error("Not implemented - use current-farm-lands.rtkq.ts");
  }

  async cropRotationList(
    id: string,
    filter?: Partial<IFilter>
  ): Promise<ICropRotationDto[]> {
    const sortString = filter?.order
      ?.map(([field, direction]) => `${String(field)} ${direction}`)
      .join(", ");
    return fetchAllPages(
      (pageNumber, pageSize) =>
        this.request<PagedResponseDto<ICropRotationDto>, string>("CropRotationsV2", {
          method: "GET",
          searchParams: {
            pageNumber,
            pageSize,
            filter: buildFilterCondition("farmLandId", "==", id),
            ...(sortString && { sort: sortString }),
          },
        }),
      CROP_ROTATIONS_FETCH_ALL_PAGE_SIZE
    );
  }
}

export const farmLandsService = new FarmLandsService();
